export type CadreSurveyField =
  | { id: string; label: string; type: 'text' | 'number' | 'date' | 'boolean'; required: boolean; enabled?: true; sensitive?: false }
  | { id: string; label: string; type: 'enum'; required: boolean; options: Array<{ value: string; label: string }>; enabled?: true; sensitive?: false };

export interface CadreSurveySession {
  id: string;
  program_id: string;
  survey_id: string;
  status: 'active' | 'completed';
  cursor: number;
  total: number;
  record_version: number;
  expires_at: string;
  current_participant_ref: string | null;
  fields: CadreSurveyField[];
}

export interface CadreSurveySubmission {
  session_id: string;
  client_op_id: string;
  expected_cursor: number;
  participant_ref: string;
  answers: Record<string, string | number | boolean>;
}

export interface CadreSurveyReceipt {
  id: string;
  session_id: string;
  participant_ref: string;
  submitted_cursor: number;
  next_cursor: number;
  next_participant_ref: string | null;
  completed: boolean;
  accepted_at: string;
  recorded_by: string;
}

const IDENTIFIER = /^[A-Za-z0-9_-]{1,128}$/;
const HASH = /^[a-f0-9]{64}$/;
const ORG_PATH = /^\/(?:[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*)?$/;
const allowedTypes = new Set(['text', 'number', 'date', 'boolean', 'enum']);
const plain = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
const exactKeys = (value: Record<string, unknown>, required: string[], optional: string[] = []) => {
  const keys = Object.keys(value);
  return required.every(key => Object.hasOwn(value, key)) && keys.every(key => required.includes(key) || optional.includes(key));
};
const identifier = (value: unknown) => typeof value === 'string' && IDENTIFIER.test(value);
const hash = (value: unknown) => typeof value === 'string' && HASH.test(value);
const utc = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value;
const calendarDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(`${value}T00:00:00.000Z`).toISOString().slice(0, 10) === value;
const fail = (message = 'The server returned an unsupported survey intake contract.'): never => { throw new Error(message); };

export const canonicalSurveyOrgPath = (value: unknown): value is string => typeof value === 'string' && ORG_PATH.test(value);

async function operationHash(parts: readonly string[]): Promise<string> {
  if (!parts.every(part => typeof part === 'string')) fail('The survey operation identity is unavailable.');
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) fail('The browser cannot bind this survey operation securely.');
  const digest = await Reflect.apply(subtle.digest, subtle, ['SHA-256', new TextEncoder().encode(JSON.stringify(parts))]) as ArrayBuffer;
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export const expectedSurveySessionId = (tenantId: string, actorId: string, clientOpId: string) => operationHash([tenantId, actorId, clientOpId]);
export const expectedSurveyReceiptId = (sessionId: string, clientOpId: string) => operationHash([sessionId, clientOpId]);

function field(value: unknown): CadreSurveyField {
  if (!plain(value)) fail();
  const record = value as Record<string, unknown>;
  if (!exactKeys(record, ['id', 'label', 'type', 'required'], ['enabled', 'sensitive', 'options'])) fail();
  if (!identifier(record.id) || typeof record.label !== 'string' || !record.label.trim() || record.label !== record.label.trim() || record.label.length > 256 || typeof record.required !== 'boolean') fail();
  if (record.enabled !== undefined && record.enabled !== true) fail();
  if (record.sensitive !== undefined && record.sensitive !== false) fail('Sensitive survey fields are unavailable in Cadre browser entry.');
  if (typeof record.type !== 'string' || !allowedTypes.has(record.type)) fail('Specialized or sensitive survey fields are unavailable in Cadre browser entry.');
  if (record.type === 'enum') {
    if (!Array.isArray(record.options) || record.options.length < 1 || record.options.length > 100) fail();
    const rawOptions = record.options as unknown[];
    const options = rawOptions.map((option: unknown) => {
      if (!plain(option)) fail();
      const choice = option as Record<string, unknown>;
      if (!exactKeys(choice, ['value', 'label']) || typeof choice.value !== 'string' || !choice.value || choice.value.length > 256 || typeof choice.label !== 'string' || !choice.label.trim() || choice.label !== choice.label.trim() || choice.label.length > 256) fail();
      return { value: choice.value as string, label: choice.label as string };
    });
    if (new Set(options.map(option => option.value)).size !== options.length) fail();
    return { id: record.id as string, label: record.label as string, type: 'enum', required: record.required as boolean, options, ...(record.enabled === true ? { enabled: true as const } : {}), ...(record.sensitive === false ? { sensitive: false as const } : {}) };
  }
  if (Object.hasOwn(record, 'options')) fail();
  return { id: record.id as string, label: record.label as string, type: record.type as 'text' | 'number' | 'date' | 'boolean', required: record.required as boolean, ...(record.enabled === true ? { enabled: true as const } : {}), ...(record.sensitive === false ? { sensitive: false as const } : {}) };
}

export function surveySession(value: unknown, expectedSurveyId: string, expectedSessionId: string, now = Date.now()): CadreSurveySession {
  if (!plain(value)) fail();
  const record = value as Record<string, unknown>;
  if (!exactKeys(record, ['id', 'program_id', 'survey_id', 'status', 'cursor', 'total', 'record_version', 'expires_at', 'current_participant_ref', 'fields'])) fail();
  if (!hash(expectedSessionId) || record.id !== expectedSessionId || !identifier(record.program_id) || record.survey_id !== expectedSurveyId || !identifier(record.survey_id)) fail();
  if (!Number.isSafeInteger(record.cursor) || !Number.isSafeInteger(record.total) || !Number.isSafeInteger(record.record_version) || (record.cursor as number) < 0 || (record.total as number) < 1 || (record.cursor as number) > (record.total as number) || (record.record_version as number) < 1) fail();
  if (!utc(record.expires_at) || Date.parse(record.expires_at as string) <= now || !['active', 'completed'].includes(String(record.status))) fail();
  const completed = record.status === 'completed';
  if (completed !== (record.cursor === record.total) || (completed ? record.current_participant_ref !== null : !hash(record.current_participant_ref))) fail();
  if (!Array.isArray(record.fields) || record.fields.length < 1 || record.fields.length > 64) fail();
  const fields = (record.fields as unknown[]).map(field);
  if (new Set(fields.map(item => item.id)).size !== fields.length) fail();
  return { ...(record as unknown as CadreSurveySession), fields };
}

export function surveyAnswers(fields: CadreSurveyField[], draft: Record<string, string>): Record<string, string | number | boolean> {
  if (Object.keys(draft).some(id => !fields.some(field => field.id === id))) fail('The draft contains an unpublished survey field.');
  const answers: Record<string, string | number | boolean> = {};
  for (const field of fields) {
    const raw = draft[field.id] ?? '';
    if (raw === '') {
      if (field.required) fail(`Answer required: ${field.label}.`);
      continue;
    }
    if (field.type === 'number') {
      const value = Number(raw);
      if (!Number.isFinite(value)) fail(`Finite number required: ${field.label}.`);
      answers[field.id] = value;
    } else if (field.type === 'boolean') {
      if (raw !== 'true' && raw !== 'false') fail(`Choose yes or no: ${field.label}.`);
      answers[field.id] = raw === 'true';
    } else if (field.type === 'date') {
      if (!calendarDate(raw)) fail(`Valid calendar date required: ${field.label}.`);
      answers[field.id] = raw;
    } else if (field.type === 'enum') {
      if (!field.options.some(option => option.value === raw)) fail(`Published choice required: ${field.label}.`);
      answers[field.id] = raw;
    } else {
      if (raw.length > 4096) fail(`Answer is too long: ${field.label}.`);
      answers[field.id] = raw;
    }
  }
  return answers;
}

export function surveyReceipt(value: unknown, request: CadreSurveySubmission, session: CadreSurveySession, actorId: string, expectedReceiptId: string): CadreSurveyReceipt {
  if (!plain(value)) fail();
  const record = value as Record<string, unknown>;
  if (!exactKeys(record, ['id', 'session_id', 'participant_ref', 'submitted_cursor', 'next_cursor', 'next_participant_ref', 'completed', 'accepted_at', 'recorded_by'])) fail();
  if (!hash(expectedReceiptId) || record.id !== expectedReceiptId || record.session_id !== request.session_id || record.participant_ref !== request.participant_ref || record.submitted_cursor !== request.expected_cursor || record.next_cursor !== request.expected_cursor + 1 || record.recorded_by !== actorId || !identifier(record.recorded_by) || typeof record.completed !== 'boolean' || !utc(record.accepted_at)) fail();
  if ((record.completed as boolean) !== (record.next_cursor === session.total) || (record.completed ? record.next_participant_ref !== null : !hash(record.next_participant_ref))) fail();
  return record as unknown as CadreSurveyReceipt;
}
